import { Component, OnInit, ViewEncapsulation, ChangeDetectorRef, Input } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, take, distinctUntilChanged } from 'rxjs/operators';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Account } from 'app/core/models/account.types';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { Contact, Category } from 'app/modules/contacts/contacts.types';
import { AccountService } from 'app/core';
import pick from 'lodash-es/pick';

@Component({
  selector: 'add-contact',
  templateUrl: './add-contact.component.html',
  styleUrls: ['./add-contact.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AddContactComponent implements OnInit {
  public categories: Category[];
  contacts: Contact[];
  contactsCount: number = 0;
  account: Account;
  contactId: null;

  public contactResults: any[] | null;
  public companyResults: any[] | null;
  private _unsubscribeAll: Subject<void>;

  @Input() parentForm: UntypedFormGroup;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _projectsService: ProjectsService,
    private _contactsService: ContactsService,
    private _accountService: AccountService,
  ) {
    this._unsubscribeAll = new Subject();

    this._accountService.currentAccount.subscribe((accountData) => {
      this.account = accountData;
    });
  }

  ngOnInit(): void {
    this._projectsService.contacts$.pipe(takeUntil(this._unsubscribeAll)).subscribe((contacts: Contact[]) => {
      if (contacts) {
        this.contacts = contacts;
        this.contactsCount = contacts.length;
      }

      if (this.contactId) {
        const currentContact = contacts.find((contact) => contact.id == this.contactId);
        this.parentForm.patchValue(
          pick(currentContact, 'id', 'name', 'email', 'category_id', 'company_name', 'category_name', 'phone'),
        );
      }

      this._changeDetectorRef.markForCheck();
    });

    this._accountService.categories$.pipe(takeUntil(this._unsubscribeAll)).subscribe((categories: Category[]) => {
      if (categories) {
        this.categories = categories.filter((option) => !option.hidden);
      }

      // Mark for check
      this._changeDetectorRef.markForCheck();
    });

    // autocomplete search for contacts
    this.parentForm
      .get('name')
      .valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(400), distinctUntilChanged())
      .subscribe((value) => {
        let query = value;
        if (!query?.id) {
          this.parentForm.reset();
          this.parentForm.get('id').setValue('');
          this.parentForm.get('name').setValue(query);

          this._contactsService.getContacts(1, 20, 'first_name', 'asc', query).subscribe((response) => {
            this.contactResults = response.contacts;
            this._changeDetectorRef.markForCheck();
          });

          this.parentForm.get('email').enable();
          this.parentForm.get('phone').enable();
          this.parentForm.get('company_name').enable();
          this.parentForm.get('category_id').enable();
        } else {
          this.parentForm.get('email').disable();
          this.parentForm.get('phone').disable();
          this.parentForm.get('company_name').disable();
          this.parentForm.get('category_id').disable();
        }
      });

    // autocomplete company names
    this.parentForm
      .get('company_name')
      .valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(400), distinctUntilChanged())
      .subscribe((value) => {
        let query = value;
        if (!query?.id) {
          this._accountService.typeaheadSearch(query, 'Company').subscribe((response) => {
            this.companyResults = response.companies;
            this._changeDetectorRef.markForCheck();
          });
        }
      });
  }

  removeEmailField(index: number, contactId: number): void {
    this._projectsService.project$.pipe(take(1)).subscribe((project) => {
      this._projectsService.deleteContactAssociation(contactId, project.id).subscribe();
      this._changeDetectorRef.markForCheck();
    });
  }

  autocompleteSelected($event): void {
    this.parentForm.patchValue($event.option.value, { emitEvent: false });
  }

  autocompleteCompanySelected($event): void {
    this.parentForm.patchValue($event.option.value, { emitEvent: false });
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  displayFn(contact: Contact): string {
    return contact && contact.name ? contact.name : '';
  }
}
